/*
 * /settings — hồ sơ học viên: họ tên, thông tin học tập, sở thích & bio,
 * thông tin bố mẹ (email bố mẹ dùng để đăng nhập xem trang /family).
 * GET/POST /api/profile (qua gateway). Xem rồi bấm "Điều chỉnh" mới sửa được.
 */
import { useEffect, useState, type FormEvent } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Field, FieldDescription, FieldGroup, FieldSet } from "@/components/ui/field"
import { Textarea } from "@/components/ui/textarea"
import { Skeleton } from "@/components/ui/skeleton"
import { Icon } from "@/components/icon"
import { ErrorState, LoadingState, Page, PageHeader, StatusNote, UserAvatar, usePageTitle } from "@/components/page"
import { useSession } from "@/app/session"
import { api, loginUrl, post } from "@/lib/api"
import {
  GENDERS, GRADES, Label, PROVINCES, SelectField, TextField,
  emptyGuardian, emptyProfile, emptySocial,
  type Guardian, type LearnerProfile, type SocialProfile,
} from "@/routes/settings/fields"

type Guardians = Record<"father" | "mother", Guardian>
const emptyGuardians = (): Guardians => ({ father: emptyGuardian("father"), mother: emptyGuardian("mother") })

function readProfile(data: any) {
  const guardians = emptyGuardians()
  for (const g of data?.guardians || []) {
    if (g.relationship === "father" || g.relationship === "mother") guardians[g.relationship as "father" | "mother"] = { ...guardians[g.relationship as "father" | "mother"], ...g }
  }
  const lp = data?.learner_profile || {}
  return {
    learner: { ...emptyProfile, ...lp, grade_level: lp.grade_level ? String(lp.grade_level) : "" } as LearnerProfile,
    social: { ...emptySocial, ...data?.social_profile } as SocialProfile,
    guardians,
  }
}

function GuardianCard({
  relationship, label, guardian, disabled, onChange,
}: {
  relationship: "father" | "mother"
  label: string
  guardian: Guardian
  disabled: boolean
  onChange: (key: "full_name" | "email" | "phone_zalo" | "area", value: string) => void
}) {
  const p = `guardian-${relationship}`
  return (
    <FieldSet disabled={disabled} className="rounded-2xl border-[1.5px] border-line bg-paper p-4 sm:p-5">
      <div className="flex items-center gap-3">
        <span className="grid size-10 place-items-center rounded-xl bg-soft">
          <Icon name="user" className="size-5" />
        </span>
        <span className="font-heading text-lg font-semibold text-ink">{label}</span>
      </div>
      <FieldGroup className="gap-4">
        <TextField id={`${p}-name`} label="Họ và tên đầy đủ" value={guardian.full_name} onChange={(v) => onChange("full_name", v)} placeholder={`Tên của ${label.toLowerCase()}`} autoComplete={`section-${relationship} name`} />
        <TextField id={`${p}-email`} type="email" label="Email dùng để đăng nhập Simba" value={guardian.email} onChange={(v) => onChange("email", v)} placeholder="name@gmail.com" autoComplete={`section-${relationship} email`} />
        <TextField id={`${p}-phone`} type="tel" label="Số điện thoại / Zalo" value={guardian.phone_zalo} onChange={(v) => onChange("phone_zalo", v)} placeholder="09xx xxx xxx" autoComplete={`section-${relationship} tel`} />
        <TextField id={`${p}-area`} label="Khu vực sinh sống" value={guardian.area} onChange={(v) => onChange("area", v)} placeholder="Ví dụ: Cầu Giấy, Hà Nội" autoComplete={`section-${relationship} address-level2`} hint="Chỉ cần quận/huyện và tỉnh/thành phố." />
      </FieldGroup>
    </FieldSet>
  )
}

function SectionCard({ eyebrow, title, description, children }: { eyebrow: string; title: string; description: React.ReactNode; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <p className="text-sm font-bold tracking-[0.08em] text-orange uppercase">{eyebrow}</p>
        <CardTitle className="font-heading text-xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

export default function SettingsPage() {
  usePageTitle("Settings", "Hồ sơ học viên Simba: thông tin học tập, sở thích và thông tin bố mẹ.")
  const { loading, user } = useSession()
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState("")
  const [name, setName] = useState("")
  const [learner, setLearner] = useState<LearnerProfile>(emptyProfile)
  const [social, setSocial] = useState<SocialProfile>(emptySocial)
  const [guardians, setGuardians] = useState<Guardians>(emptyGuardians)
  const [snapshot, setSnapshot] = useState<any>(null)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<{ tone: "success" | "error"; text: string } | null>(null)
  const [tried, setTried] = useState(false)

  const apply = (data: any, fullName: string) => {
    const p = readProfile(data)
    setLearner(p.learner)
    setSocial(p.social)
    setGuardians(p.guardians)
    setName(fullName)
    setSnapshot({ data, fullName })
  }

  const load = () => {
    setLoadError("")
    api("/api/profile")
      .then((data: any) => {
        apply(data, user.full_name || "")
        setLoaded(true)
      })
      .catch((e: any) => {
        if (e.status === 401) location.href = loginUrl(location.href)
        else setLoadError(e.message)
      })
  }

  useEffect(() => {
    if (loading) return
    if (!user) {
      location.href = loginUrl(location.href)
      return
    }
    load()
  }, [loading, user?.id])

  if (loading || !user) return <LoadingState label="Đang tải hồ sơ…" />

  const setL = (k: keyof LearnerProfile, v: string) => setLearner((c) => ({ ...c, [k]: v }))
  const setS = (k: keyof SocialProfile, v: string) => setSocial((c) => ({ ...c, [k]: v }))
  const setG = (r: "father" | "mother") => (k: "full_name" | "email" | "phone_zalo" | "area", v: string) =>
    setGuardians((c) => ({ ...c, [r]: { ...c[r], [k]: v } }))

  const cancel = () => {
    if (snapshot) apply(snapshot.data, snapshot.fullName)
    setEditing(false)
    setTried(false)
    setStatus(null)
  }

  const save = async (e: FormEvent) => {
    e.preventDefault()
    if (!editing) return
    setTried(true)
    if (!learner.gender || !learner.grade_level) {
      setStatus({ tone: "error", text: "Con chọn giúp giới tính và lớp đang học nhé." })
      return
    }
    setBusy(true)
    setStatus(null)
    try {
      const data: any = await post("/api/profile", {
        full_name: name,
        learner_profile: { ...learner, grade_level: Number(learner.grade_level) },
        social_profile: social,
        guardians: [guardians.father, guardians.mother],
      })
      apply(data, name)
      setEditing(false)
      setTried(false)
      setStatus({ tone: "success", text: "Đã lưu hồ sơ. Con có thể điều chỉnh lại bất cứ lúc nào." })
    } catch (err: any) {
      setStatus({ tone: "error", text: err.message })
    } finally {
      setBusy(false)
    }
  }

  const ro = !editing

  return (
    <Page className="max-w-[880px]">
      <Link to="/my-courses" className="inline-flex w-fit items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-orange-strong">
        <Icon name="arrow-left" className="size-4" />
        Khóa học của tôi
      </Link>
      <PageHeader
        eyebrow="Hồ sơ học viên"
        title="Settings"
        description="Thông tin này giúp Simba hiểu và đồng hành đúng với con."
        actions={
          loaded && !editing ? (
            <Button
              onClick={() => {
                setEditing(true)
                setStatus(null)
              }}
            >
              <Icon name="settings" className="size-4" />
              Điều chỉnh
            </Button>
          ) : undefined
        }
      />

      {loadError && <ErrorState message={loadError} onRetry={load} />}
      {!loaded && !loadError && (
        <div className="flex flex-col gap-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-48 rounded-[20px]" />
          ))}
        </div>
      )}

      {loaded && (
        <form onSubmit={save} className="flex flex-col gap-6">
          {!editing && status && <StatusNote tone={status.tone}>{status.text}</StatusNote>}

          <Card>
            <CardContent className="flex flex-col gap-5 sm:flex-row sm:items-start">
              <UserAvatar user={user} size="lg" className="size-16" />
              <FieldGroup className="min-w-0 flex-1 gap-4">
                <TextField id="full-name" label="Họ và tên" value={name} onChange={setName} placeholder="Ví dụ: Nguyễn Tất Đắc" autoComplete="name" disabled={ro} hint={<>Email đăng nhập: <span className="break-all">{user.email}</span></>} />
                {editing && (
                  <p className="text-sm text-muted-foreground">
                    <span className="text-orange">*</span> Trường bắt buộc
                  </p>
                )}
              </FieldGroup>
            </CardContent>
          </Card>

          <SectionCard eyebrow="Thông tin học tập" title="Thông tin của con" description="Lớp học được tính tại ngày 01.10.2026.">
            <FieldSet disabled={ro}>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField id="dob" type="date" label="Ngày tháng năm sinh" value={learner.date_of_birth} onChange={(v) => setL("date_of_birth", v)} max={new Date().toISOString().slice(0, 10)} />
                <SelectField id="gender" label="Giới tính" value={learner.gender} onChange={(v) => setL("gender", v)} items={GENDERS} placeholder="Chọn giới tính" disabled={ro} invalid={tried && !learner.gender} />
                <TextField id="province" label="Tỉnh / Thành phố" value={learner.province_city} onChange={(v) => setL("province_city", v)} list="province-city-suggestions" placeholder="Ví dụ: Hà Nội" autoComplete="address-level1" />
                <SelectField id="grade" label="Lớp tại ngày 01.10.2026" value={learner.grade_level} onChange={(v) => setL("grade_level", v)} items={GRADES} placeholder="Chọn lớp" disabled={ro} invalid={tried && !learner.grade_level} />
                <TextField id="school" className="sm:col-span-2" label="Trường đang học" value={learner.school_name} onChange={(v) => setL("school_name", v)} placeholder="Ví dụ: Trường THCS Nguyễn Du" autoComplete="organization" />
              </div>
              <datalist id="province-city-suggestions">
                {PROVINCES.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </FieldSet>
          </SectionCard>

          <SectionCard eyebrow="Sở thích & giới thiệu" title="Một chút về con" description="Những thông tin này sẽ xuất hiện trong profile mà các bạn cùng khóa có thể xem.">
            <FieldSet disabled={ro}>
              <div className="grid gap-4 sm:grid-cols-3">
                <TextField id="food" label="Đồ ăn yêu thích" value={social.favorite_food} onChange={(v) => setS("favorite_food", v)} placeholder="Ví dụ: Phở bò" maxLength={120} />
                <TextField id="movie" label="Bộ phim yêu thích" value={social.favorite_movie} onChange={(v) => setS("favorite_movie", v)} placeholder="Ví dụ: Inside Out" maxLength={160} />
                <TextField id="music" label="Band / Nhóm nhạc yêu thích" value={social.favorite_music} onChange={(v) => setS("favorite_music", v)} placeholder="Ví dụ: Imagine Dragons" maxLength={160} />
                <Field className="sm:col-span-3">
                  <Label htmlFor="bio" optional>Bio</Label>
                  <Textarea id="bio" value={social.bio} onChange={(e) => setS("bio", e.target.value.slice(0, 500))} placeholder="Con thích viết gì về mình thì viết ở đây nhé…" maxLength={500} rows={4} />
                  <FieldDescription className="text-right">{social.bio.length}/500 ký tự</FieldDescription>
                </Field>
              </div>
            </FieldSet>
          </SectionCard>

          <SectionCard
            eyebrow="Chia sẻ với gia đình"
            title="Thông tin bố và mẹ"
            description={
              <>
                Bố mẹ đăng nhập Simba bằng đúng email con khai báo mới xem được thông tin học tập của con.{" "}
                <strong className="text-ink">Con có thể điều chỉnh thông tin này bất cứ lúc nào.</strong>
              </>
            }
          >
            <div className="grid gap-4 md:grid-cols-2">
              <GuardianCard relationship="father" label="Bố" guardian={guardians.father} disabled={ro} onChange={setG("father")} />
              <GuardianCard relationship="mother" label="Mẹ" guardian={guardians.mother} disabled={ro} onChange={setG("mother")} />
            </div>
          </SectionCard>

          {editing && (
            <div className="sticky bottom-4 z-10 flex flex-col gap-3 rounded-[20px] border-[1.5px] border-line bg-paper/95 p-4 shadow-card backdrop-blur sm:flex-row sm:items-center">
              <span className="flex-1 text-sm text-muted-foreground">Kiểm tra lại rồi bấm lưu nhé.</span>
              {status && <StatusNote tone={status.tone}>{status.text}</StatusNote>}
              <div className="flex gap-2">
                <Button type="button" variant="outline" disabled={busy} onClick={cancel}>
                  Hủy
                </Button>
                <Button type="submit" disabled={busy}>
                  {busy ? "Đang lưu…" : "Lưu thay đổi"}
                </Button>
              </div>
            </div>
          )}
        </form>
      )}
    </Page>
  )
}
